import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { Maximize2, Minimize2, Pause, Play, RadioTower, Volume2, VolumeX } from 'lucide-react'
import audioStyles from 'components/answers/FileEmbed/audio.module.scss'
import broadcastStyles from 'components/answers/radio-broadcast-transport.module.scss'
import { combinedMediaAudioScrimIsCompact } from 'lib/combinedMedia'
import { panelSwipeExclusionProps } from 'components/shared/panelSwipeExclusions'
import VideoSettingsMenu from 'components/shared/VideoSettingsMenu'
import MediaDockProgress from './MediaDockProgress'
import styles from './media-dock.module.scss'

const stopEvent = event => event.stopPropagation()

/**
 * The expanded dock's now-playing tile wears the combined image+audio card's
 * scrim transport — the 52px play control, both times around the seek, the
 * volume flyout — drawn with the audio player's own classes. The seek is the
 * dock's plain timeline rather than the card's wave: it spans the tile and, in
 * cinema, the window, where a wave reads as a flat line. The controls drive
 * the dock's runtime through the handlers the dock already owns; nothing here
 * mounts a player of its own.
 *
 * The tile underneath is one big pointer to the song's thread, so every
 * control stops its own click and the strip lets clicks on its inert gaps
 * through, exactly as the post tile does.
 *
 * Expanded, it takes the radio card's two rows: the seek along the top, and
 * "time / duration" with the controls under it, ending in `actions` (the
 * playlist and upvalue buttons). On the live broadcast the play control is the
 * radio tower, as on the Music header's card.
 *
 * In cinema the strip also carries what the video player's own chrome has:
 * the settings gear (speed, captions) and fullscreen. Both are opt-in through
 * their handlers; the expanded tile passes none.
 *
 * @param {{
 *   mediaKey: string,
 *   isPlaying: boolean,
 *   isDocumentVisible: boolean,
 *   liveRadio: boolean,
 *   onTogglePlayback: () => void,
 *   onDraggingChange: (dragging: boolean) => void,
 *   volume: number,
 *   isMuted: boolean,
 *   onVolumeChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
 *   onToggleMute: () => void,
 *   onVolumeMouseLeave?: (event: React.MouseEvent) => void,
 *   playbackRate?: number,
 *   playbackRates?: number[],
 *   onPlaybackRateChange?: (rate: number) => void,
 *   captionsAvailable?: boolean,
 *   captionsEnabled?: boolean,
 *   onToggleCaptions?: () => void,
 *   onSettingsOpenChange?: (open: boolean) => void,
 *   isFullscreen?: boolean,
 *   onFullscreen?: () => void,
 *   actions?: React.ReactNode,
 * }} props
 */
export default function MediaDockStageTransport({
    mediaKey,
    isPlaying,
    isDocumentVisible,
    liveRadio,
    onTogglePlayback,
    onDraggingChange,
    volume,
    isMuted,
    onVolumeChange,
    onToggleMute,
    onVolumeMouseLeave = undefined,
    playbackRate = 1,
    playbackRates = undefined,
    onPlaybackRateChange = undefined,
    captionsAvailable = false,
    captionsEnabled = false,
    onToggleCaptions = undefined,
    onSettingsOpenChange = undefined,
    isFullscreen = false,
    onFullscreen = undefined,
    actions = null,
}) {
    const rootRef = useRef(null)
    // Same breakpoint as the post tile: a very narrow strip drops its duplicate
    // time labels rather than squeezing the wave.
    const [isCompact, setIsCompact] = useState(false)

    useEffect(() => {
        const el = rootRef.current
        if (!el || typeof ResizeObserver === 'undefined') return undefined

        const syncCompact = () => {
            const nextCompact = combinedMediaAudioScrimIsCompact(el.clientWidth)
            setIsCompact(previous => (previous === nextCompact ? previous : nextCompact))
        }
        syncCompact()
        const observer = new ResizeObserver(syncCompact)
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    // Cinema keeps the one-row strip; the expanded tile stacks.
    const stacked = !onFullscreen
    const VolumeIcon = isMuted ? VolumeX : Volume2
    const FullscreenIcon = isFullscreen ? Minimize2 : Maximize2

    const controls = (
        <div className={audioStyles.audioActions}>
            {/* Click always mute-toggles; the slider pops on hover/focus
                for fine pointers, the way the rest of the dock's volume
                controls behave. */}
            <div
                className={cn(audioStyles.audioVolumeControl, audioStyles.audioVolumeControlFlyout)}
                onMouseLeave={onVolumeMouseLeave}
                {...panelSwipeExclusionProps}
            >
                <button
                    type="button"
                    className={audioStyles.audioVolumeButton}
                    onClick={event => {
                        event.stopPropagation()
                        onToggleMute()
                    }}
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                    title={isMuted ? 'Unmute' : 'Mute'}
                >
                    <VolumeIcon className={audioStyles.audioVolumeIcon} aria-hidden="true" />
                </button>
                <div className={audioStyles.audioVolumeFlyout} data-open="false">
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        className={audioStyles.audioVolumeFlyoutRange}
                        style={
                            /** @type {React.CSSProperties} */ ({
                                '--audio-volume': `${Math.round(volume * 100)}%`,
                            })
                        }
                        onChange={onVolumeChange}
                        onInput={onVolumeChange}
                        onClick={stopEvent}
                        aria-label="Volume"
                        aria-orientation="vertical"
                    />
                </div>
            </div>
            {onPlaybackRateChange || (captionsAvailable && onToggleCaptions) ? (
                <span {...panelSwipeExclusionProps}>
                    <VideoSettingsMenu
                        buttonClassName={audioStyles.audioVolumeButton}
                        iconClassName={audioStyles.audioVolumeIcon}
                        playbackRate={playbackRate}
                        playbackRates={playbackRates}
                        onPlaybackRateChange={onPlaybackRateChange}
                        captionsAvailable={captionsAvailable}
                        captionsEnabled={captionsEnabled}
                        onToggleCaptions={onToggleCaptions}
                        onMouseLeave={onVolumeMouseLeave}
                        onOpenChange={onSettingsOpenChange}
                        dataTestId="media-dock-stage-settings"
                    />
                </span>
            ) : null}
            {onFullscreen ? (
                <button
                    type="button"
                    className={audioStyles.audioVolumeButton}
                    onClick={event => {
                        event.stopPropagation()
                        onFullscreen()
                    }}
                    aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                    title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                    data-testid="media-dock-stage-fullscreen"
                >
                    <FullscreenIcon className={audioStyles.audioVolumeIcon} aria-hidden="true" />
                </button>
            ) : null}
            {actions}
        </div>
    )

    return (
        <div
            ref={rootRef}
            className={cn(
                audioStyles.customAudioPlayer,
                audioStyles.customAudioPlayerScrimTheme,
                stacked && audioStyles.customAudioPlayerScrimLayout,
                isCompact && audioStyles.customAudioPlayerScrimCompact,
                styles.stageTransport,
            )}
            data-testid="media-dock-stage-transport"
        >
            <button
                type="button"
                // Tuned in, the tower is green, as on the Music header's tuner.
                className={cn(
                    audioStyles.audioPlayButton,
                    liveRadio && isPlaying && broadcastStyles.tunerOn,
                )}
                onClick={event => {
                    event.stopPropagation()
                    onTogglePlayback()
                }}
                aria-label={
                    liveRadio ? (isPlaying ? 'Tune out' : 'Tune in') : isPlaying ? 'Pause' : 'Play'
                }
                data-testid="media-dock-stage-play"
            >
                {liveRadio ? (
                    <RadioTower className={audioStyles.audioPlayIcon} aria-hidden="true" />
                ) : isPlaying ? (
                    <Pause className={audioStyles.audioPlayIcon} aria-hidden="true" />
                ) : (
                    <Play
                        className={cn(audioStyles.audioPlayIcon, audioStyles.audioPlayIconPlay)}
                        aria-hidden="true"
                    />
                )}
            </button>

            {stacked ? (
                <MediaDockProgress
                    key={mediaKey}
                    mediaKey={mediaKey}
                    isPlaying={isPlaying}
                    isDocumentVisible={isDocumentVisible}
                    isExpanded
                    liveRadio={liveRadio}
                    onDraggingChange={onDraggingChange}
                    variant="stacked"
                >
                    {controls}
                </MediaDockProgress>
            ) : (
                <>
                    <div className={audioStyles.audioTimelineGroup}>
                        <MediaDockProgress
                            key={mediaKey}
                            mediaKey={mediaKey}
                            isPlaying={isPlaying}
                            isDocumentVisible={isDocumentVisible}
                            isExpanded
                            variant="seek"
                            liveRadio={liveRadio}
                            onDraggingChange={onDraggingChange}
                        />
                    </div>
                    {controls}
                </>
            )}
        </div>
    )
}
